import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import { UpdatedTermsState } from '@platform-frontend/account';
import {
  AssetState,
  CHECKOUT_MATOMO_EVENT_CATEGORY_PURCHASE,
  ConfirmSubscriptionEulaUpdate,
  ConfirmSubscriptionPriceUpdate,
  FlowState,
  GotoCommercialCheckout,
  MSG_PRICE_UPDATES_SINGLE_INCREASE,
  ProductState,
  SubscriptionBillingState,
  SubscriptionPaymentState
} from '@platform-frontend/checkout';
import { SubscriptionBookingStatus } from '@platform-frontend/checkout-drop-in';
import {
  BillingAddress,
  MSG_PRICE_UPDATES_SINGLE_DECREASE,
  MSG_TERMS_UPDATES_SINGLE,
  PaymentMethod,
  SubscriptionDetails,
  SubscriptionPriceUpdateService
} from '@platform-frontend/marketplace';
import {
  EULA_UPDATE_NOTIFICATION_PRIORIZATION_LEVEL,
  PRICE_DECREASE_NOTIFICATION_PRIORIZATION_LEVEL,
  PRICE_INCREASE_NOTIFICATION_PRIORIZATION_LEVEL
} from '@platform-frontend/platform';
import { LoadingState } from '@platform-frontend/ui-tools';
import { CloseWindow, Navigate, NotificationOptions, ShowInfoNotification, ShowWarningNotification } from '@platform-frontend/util';
import { Observable, of } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'yat-overview',
  templateUrl: './overview.component.html',
  styleUrls: ['./overview.component.scss']
})
export class OverviewComponent implements OnInit {
  constructor(private activatedRoute: ActivatedRoute, private subscriptionPriceUpdateService: SubscriptionPriceUpdateService, private store: Store) {}

  @Select(ProductState.billingAddressRequired)
  billingAddressRequired$: Observable<boolean>;

  @Select(SubscriptionPaymentState.connectedPaymentMethod)
  connectedPaymentMethod$: Observable<PaymentMethod | null>;

  @Select(SubscriptionBillingState.connectedBillingAddress)
  connectedBillingAddress$: Observable<BillingAddress | null>;

  @Select(AssetState.subscriptionDetails)
  subscriptionDetails$: Observable<SubscriptionDetails | null>;

  @Select(AssetState.subscriptionBookingStatus)
  subscriptionBookingStatus$: Observable<SubscriptionBookingStatus | null>;

  @Select(ProductState.isLive)
  isLive$: Observable<boolean>;

  readonly MATOMO_EVENT_CATEGORY_PURCHASE = CHECKOUT_MATOMO_EVENT_CATEGORY_PURCHASE;

  ngOnInit(): void {
    // TODO MP-7526: Refactor to show notifications after terms update notification
    this.store
      .select(LoadingState.isLoading)
      .pipe(
        filter(isLoading => !isLoading),
        take(1),
        switchMap(() => this.store.selectOnce(UpdatedTermsState.arePlatformTermsUpdated)),
        switchMap(termsUpdated => (termsUpdated ? of(null) : this.showPriceOrEulaUpdateNotification())),
        untilDestroyed(this)
      )
      .subscribe();
  }

  private showPriceOrEulaUpdateNotification(): Observable<unknown> {
    const confirmedPricing = this.store.selectSnapshot(AssetState.confirmedPricingInformation);
    const confirmedTermsVersion = this.store.selectSnapshot(AssetState.confirmedTermsVersion);
    const newPricing = this.store.selectSnapshot(AssetState.pricingInformation);
    const licenseType = this.store.selectSnapshot(AssetState.licenseType);
    const product = this.store.selectSnapshot(ProductState.product);

    if (confirmedPricing && newPricing && licenseType && licenseType.kind.type === 'SUBSCRIPTION' && product && confirmedPricing.total !== newPricing.total) {
      const notificationOptions: Partial<NotificationOptions> = {
        onDismiss: () => this.store.dispatch(new ConfirmSubscriptionPriceUpdate()).pipe(switchMap(() => this.showPriceOrEulaUpdateNotification()))
      };
      if (confirmedPricing.total < newPricing.total) {
        const message = this.subscriptionPriceUpdateService.buildSinglePriceUpdateNotificationMessage(
          MSG_PRICE_UPDATES_SINGLE_INCREASE,
          product.productLocalization.productName,
          confirmedPricing,
          newPricing,
          licenseType.kind.subscriptionInterval
        );
        return this.store.dispatch(
          new ShowWarningNotification(message, { priorizationLevel: PRICE_INCREASE_NOTIFICATION_PRIORIZATION_LEVEL, ...notificationOptions })
        );
      } else {
        const message = this.subscriptionPriceUpdateService.buildSinglePriceUpdateNotificationMessage(
          MSG_PRICE_UPDATES_SINGLE_DECREASE,
          product.productLocalization.productName,
          confirmedPricing,
          newPricing,
          licenseType.kind.subscriptionInterval
        );
        return this.store.dispatch(
          new ShowInfoNotification(message, { priorizationLevel: PRICE_DECREASE_NOTIFICATION_PRIORIZATION_LEVEL, ...notificationOptions })
        );
      }
    } else if (
      confirmedTermsVersion &&
      product &&
      product.productLocalization.termsVersion &&
      confirmedTermsVersion !== product.productLocalization.termsVersion
    ) {
      const notificationOptions: Partial<NotificationOptions> = {
        onDismiss: new ConfirmSubscriptionEulaUpdate(),
        priorizationLevel: EULA_UPDATE_NOTIFICATION_PRIORIZATION_LEVEL
      };
      return this.store.dispatch(
        new ShowInfoNotification(MSG_TERMS_UPDATES_SINGLE(product.productLocalization.productName, product.productLocalization.termsPath), notificationOptions)
      );
    } else {
      return of(null);
    }
  }

  managePaymentMethod(): void {
    this.store.dispatch(new Navigate(['../payment-method-selection'], { relativeTo: this.activatedRoute }));
  }

  manageBillingAddress(): void {
    this.store.dispatch(new Navigate(['../billing-address-selection'], { relativeTo: this.activatedRoute }));
  }

  createBillingAddress(): void {
    this.store.dispatch(new Navigate(['../billing-address'], { relativeTo: this.activatedRoute }));
  }

  close(): void {
    this.store.dispatch(new CloseWindow());
  }

  switchToResubscribe(): void {
    const flow = this.store.selectSnapshot(FlowState.flow);
    if (flow.type === 'SUBSCRIPTION_MANAGEMENT') this.store.dispatch(new GotoCommercialCheckout(flow.startParams));
  }
}
